import type { CatalogApp, CatalogResponse, MeResponse, TeamResponse, TeamUser, TenantDetail, TenantSummary } from './types.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface NewPerson {
  email: string;
  name: string;
  roles: string[];
}

export interface Api {
  me(): Promise<MeResponse>;
  // Team (Mandanten-Admin)
  team(): Promise<TeamResponse>;
  invite(input: NewPerson): Promise<TeamUser>;
  updateMember(userId: string, input: { roles?: string[]; name?: string }): Promise<TeamUser>;
  removeMember(userId: string): Promise<void>;
  resendInvitation(userId: string): Promise<void>;
  // Administration (Plattform-Admin)
  tenants(): Promise<{ tenants: TenantSummary[] }>;
  tenant(tenantId: string): Promise<TenantDetail>;
  createTenant(input: { name: string; contactEmail?: string; apps: string[]; admin: NewPerson }): Promise<TenantDetail>;
  updateTenant(tenantId: string, input: { name?: string; contactEmail?: string; status?: 'active' | 'suspended' }): Promise<TenantDetail>;
  setApp(tenantId: string, appId: string, enabled: boolean): Promise<TenantDetail>;
  deleteTenant(tenantId: string, confirmName: string): Promise<TenantDetail>;
  adminInvite(tenantId: string, input: NewPerson): Promise<TeamUser>;
  adminUpdateMember(tenantId: string, userId: string, input: { roles?: string[] }): Promise<TeamUser>;
  adminRemoveMember(tenantId: string, userId: string): Promise<void>;
  catalog(): Promise<CatalogResponse>;
  putCatalogApp(app: CatalogApp): Promise<CatalogApp>;
}

/** Plattform-API unter /api auf derselben Origin (CloudFront → HTTP API). */
export function httpApi(getToken: () => Promise<string>): Api {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`/api${path}`, {
      method,
      headers: { authorization: `Bearer ${await getToken()}`, ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, data.error ?? `Fehler ${res.status}`);
    return data as T;
  }
  const t = (id: string) => `/admin/tenants/${encodeURIComponent(id)}`;
  const u = (id: string) => encodeURIComponent(id);
  return {
    me: () => call('GET', '/me'),
    team: () => call('GET', '/team/users'),
    invite: (input) => call('POST', '/team/users', input),
    updateMember: (id, input) => call('PATCH', `/team/users/${u(id)}`, input),
    removeMember: (id) => call('DELETE', `/team/users/${u(id)}`),
    resendInvitation: (id) => call('POST', `/team/users/${u(id)}/resend`),
    tenants: () => call('GET', '/admin/tenants'),
    tenant: (id) => call('GET', t(id)),
    createTenant: (input) => call('POST', '/admin/tenants', input),
    updateTenant: (id, input) => call('PATCH', t(id), input),
    setApp: (id, appId, enabled) => call(enabled ? 'PUT' : 'DELETE', `${t(id)}/apps/${u(appId)}`),
    deleteTenant: (id, confirmName) => call('DELETE', t(id), { confirmName }),
    adminInvite: (id, input) => call('POST', `${t(id)}/users`, input),
    adminUpdateMember: (id, userId, input) => call('PATCH', `${t(id)}/users/${u(userId)}`, input),
    adminRemoveMember: (id, userId) => call('DELETE', `${t(id)}/users/${u(userId)}`),
    catalog: () => call('GET', '/admin/catalog'),
    putCatalogApp: ({ appId, ...rest }) => call('PUT', `/admin/catalog/${u(appId)}`, rest),
  };
}
